import React, { useRef, useState, useEffect } from "react";
import { connect } from "react-redux";

import { Blocks } from "react-loader-spinner";
import Backdrop from "@mui/material/Backdrop";
import SignInContainer from "../../../containers/SignInContainer";
import BVASelect from "../../../components/BVASelect/BVASelect";
import BVAButton from "../../../components/BVAButton/BVAButton";
import { generateDashboardReportData } from "../../../store/modules/reports/actions";
import SingleChart from "../../../components/BVAChart/SingleChart";
import DoubleChart from "../../../components/BVAChart/DoubleChart";
import { generateForecastedRevenue, generateForecastedDOE } from "../../../store/modules/valuation/actions";
import { setReportIsLoading } from "../../../store/modules/appState/actions";

function Reports({
  user,
  reportLoading,
  forecastedRevenue,
  forecastedDOE,
  dispatchGenerateDashboardReportData,
  dispatchGenerateForecastedRevenue,
  dispatchGenerateForecastedDOE,
  dispatchSetReportIsLoading
}){
  const forecastedRevenueChartRef = useRef(null)
  const forecastedDOEChartRef = useRef(null)
  const [chartPart, setChartPart] = useState(false)
  const [dealType, setDealType] = useState("STOCK")
  const [reportType, setReportType] = useState("GENERAL")
  const [reportFormat, setReportFormat] = useState("PDF")

  // Options for each select
  const dealTypeItems = [
    { label: "Stock Sale Deal", value: "STOCK" },
    { label: "Asset Sale Deal", value: "ASSET" }
  ];

  const reportTypeItems = [
    { label: "General", value: "GENERAL" },
    { label: "Certificate", value: "CERTIFICATE" },
    { label: "Leveraged Buyout", value: "LBO" },
    { label: "Transaction", value: "TRANSACTION" },
    { label: "Valuation", value: "VALUATION" },
    { label: "Waterfall", value: "WATERFALL" }
  ];

  const reportFormatItems = [
    { label: "PDF", value: "PDF" },
    { label: "DOCX", value: "DOCX" }
  ];

  // Mounted
  useEffect(() => {
    dispatchSetReportIsLoading("false");
    dispatchGenerateForecastedRevenue();
    dispatchGenerateForecastedDOE();
    setTimeout(() => setChartPart(true), 1000);
  }, [])

  if (!user) return <SignInContainer />

  // Methods
  const handleGenerateReportData = async () => {
    try {
      dispatchSetReportIsLoading("Generating Report..");
      await dispatchGenerateDashboardReportData(
        {
          forecastedRevenueChartElement: forecastedRevenueChartRef.current,
          forecastedDOEChartElement: forecastedDOEChartRef.current,
        },
        dealType,
        reportType,
        reportFormat
      )
    } catch (error ) {

    } finally {
      dispatchSetReportIsLoading("false");
    }
  }

  return (
    <div>
      <div className="p-20">
        {reportLoading && reportLoading !== "false" ? (
          <div style={{ display: "flex", justifyContent: "center", margin: "30px" }}>
            <Backdrop
              sx={{ color: "fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={reportLoading !== "false"}
            >
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Blocks visible={true} height="80" width="80" />
                <p className="mt-5 text-white text-2xl font-bold">{reportLoading}</p>
              </div>
            </Backdrop>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto px-4 py-12">
            <div className="flex flex-col space-y-4 bg-white rounded-3xl shadow-2xl pt-20 pr-20 pl-10 pb-10 text-center">
              {/* Deal Type */}
              <div className="flex justify-center items-center gap-4">
                <label className="w-28 text-sm font-medium text-gray-700">
                  Deal Type
                </label>
                <BVASelect
                  items={dealTypeItems}
                  value={dealType}
                  width="14em"
                  placeholder="Select a section"
                  onChange={(value) => setDealType(value)}
                />
              </div>

              {/* Report Type */}
              <div className="flex justify-center items-center gap-4">
                <label className="w-28 text-sm font-medium text-gray-700">
                  Report Type
                </label>
                <BVASelect
                  items={reportTypeItems}
                  value={reportType}
                  width="14em"
                  placeholder="Select a report type"
                  onChange={(value) => setReportType(value)}
                />
              </div>

              {/* Report Format */}
              <div className="flex justify-center items-center gap-4">
                <label className="w-28 text-sm font-medium text-gray-700">
                  Report Format
                </label>
                <BVASelect
                  items={reportFormatItems}
                  value={reportFormat}
                  width="14em"
                  placeholder="Select a format"
                  onChange={(value) => setReportFormat(value)}
                />
              </div>

              <div className="pt-12 py-4 w-full flex justify-center">
                <div className="w-full max-w-md flex justify-end">
                  <BVAButton kind="primary" onClick={handleGenerateReportData}>
                    Generate Report Data
                  </BVAButton>
                </div>
              </div>
            </div>
          </div>
        )}
        {chartPart && (<div style={{ marginTop: "-500vh", positon: "absolute", pointerEvents: "none" }}>
          <div ref={forecastedRevenueChartRef}>
            <DoubleChart
              label={forecastedRevenue?.yearsTotal}
              title="Forecasted Revenue"
              yTitle="Revenue"
              name1="Sales Revenue"
              name2="YoY Change"
              series1={forecastedRevenue?.salesRevenueArray?.concat([forecastedRevenue?.forecastedValue])}
              series2={forecastedRevenue?.yoyArray}
              minYaxis={forecastedRevenue?.minYaxis}
              forecastedValue={forecastedRevenue?.forecastedValue}
            />
          </div>
          <div ref={forecastedDOEChartRef}>
            <SingleChart
              label={forecastedDOE?.yearsTotal}
              title="Forecasted Discretionary Owner Earnings"
              yTitle="DOE"
              seriesArray={forecastedDOE?.DOEArray?.concat([forecastedDOE?.forecastedValue])}
              forecastedValue={forecastedDOE?.forecastedValue}
            />
          </div>
        </div>)}
      </div>
    </div>
  )
};

const mapStateToProps = (state) => ({
  user: state.user,
  reportLoading: state.appState.reportLoading,
  forecastedRevenue: state.valuation.forecastedRevenue,
  forecastedDOE: state.valuation.forecastedDOE,
});

const mapDispatchToProps = (dispatch) => ({
  async dispatchGenerateForecastedRevenue() {
    await dispatch(generateForecastedRevenue());
  },
  async dispatchGenerateForecastedDOE() {
    await dispatch(generateForecastedDOE());
  },
  async dispatchGenerateDashboardReportData(charts, dealType, reportType, reportFormat) {
    await dispatch(generateDashboardReportData(charts, dealType, reportType, reportFormat))
  },
  async dispatchSetReportIsLoading(text) {
    await dispatch(setReportIsLoading(text));
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(Reports);
